import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Steps, Button, Space, Alert, Spin, message } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useGetFieldConfigurationForCategoryGrouped } from '@/shared/api/client/field-configuration-controller';
import { useGetCategoryById } from '@/shared/api/client/customer-category-controller';
import { GroupMemberData } from '../types';
import SingleFieldGroupForm from './SingleFieldGroupForm';
import ProductSelectionForm from './ProductSelectionForm';
import LocationSelectionForm from './LocationSelectionForm';
import DocumentUploadForm from './DocumentUploadForm';
import './GroupMemberModal.css';

const { Step } = Steps;

interface GroupMemberModalProps {
  visible: boolean;
  memberTemplateId: number | null;
  editingMember?: GroupMemberData | null;
  editingIndex?: number;
  onSave: (member: GroupMemberData, index?: number) => void;
  onCancel: () => void;
}

const GroupMemberModal: React.FC<GroupMemberModalProps> = ({
  visible,
  memberTemplateId,
  editingMember,
  editingIndex,
  onSave,
  onCancel,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [memberData, setMemberData] = useState<Record<string, any>>({});
  const [productData, setProductData] = useState<any[]>([]);
  const [locationData, setLocationData] = useState<any[]>([]);
  const [documentData, setDocumentData] = useState<any[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [allSteps, setAllSteps] = useState<any[]>([]);

  // Fetch member template category details
  const {
    data: memberCategory,
    isLoading: categoryLoading,
  } = useGetCategoryById(memberTemplateId!, {
    query: {
      enabled: !!memberTemplateId && visible,
    },
  });


  // Fetch field configuration for member template
  const {
    data: fieldConfiguration,
    isLoading: fieldsLoading,
    error: fieldsError,
  } = useGetFieldConfigurationForCategoryGrouped(
    memberTemplateId!,
    {
      query: {
        enabled: !!memberTemplateId && visible,
      },
    }
  );

  // Initialize member data when editing
  useEffect(() => {
    if (editingMember) {
      setMemberData(editingMember.data || {});
      setProductData(editingMember.products || []);
      setLocationData(editingMember.locations || []);
      setDocumentData(editingMember.documents || []);
    } else {
      setMemberData({});
      setProductData([]);
      setLocationData([]);
      setDocumentData([]);
    }
    setCurrentStep(0);
    setErrors({});
    setCompletedSteps(new Set());
  }, [editingMember, visible]);

  // Build complete steps array including field groups and additional steps
  useEffect(() => {
    if (!fieldConfiguration || !memberCategory) return;

    const steps: any[] = [];

    // Add field group steps (only visible ones)
    const fieldGroupSteps = fieldConfiguration.fieldGroups
      ?.filter(group => group.isVisible)
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
      .map(group => ({
        id: group.groupKey,
        title: group.groupName,
        description: group.description,
        type: 'fieldGroup',
        fieldGroup: group,
        isVisible: true,
      })) || [];

    steps.push(...fieldGroupSteps);

      console.log(memberCategory);


    // Add products step (members should have products)
    if (memberCategory.products && memberCategory.products.length > 0) {
      steps.push({
        id: 'products',
        title: 'Product Selection',
        description: 'Select products for this member',
        type: 'products',
        isVisible: true,
      });
    }

    // Add locations step if configured
    if (memberCategory.locations && memberCategory.locations.length > 0) {
      steps.push({
        id: 'locations',
        title: 'Address Information',
        description: 'Member address details',
        type: 'locations',
        isVisible: true,
      });
    }

    // Add documents step if configured
    if (memberCategory.documents && memberCategory.documents.length > 0) {
      steps.push({
        id: 'documents',
        title: 'Document Upload',
        description: 'Upload required member documents',
        type: 'documents',
        isVisible: true,
      });
    }

    setAllSteps(steps);
  }, [fieldConfiguration, memberCategory]);

  const handleFieldChange = (fieldName: string, value: any) => {
    setMemberData(prev => ({
      ...prev,
      [fieldName]: value,
    }));
    
    // Clear error for this field
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const validateCurrentStep = (): boolean => {
    const currentStepData = allSteps[currentStep];
    if (!currentStepData) return true;

    const newErrors: Record<string, string> = {};
    let isValid = true;

    // Validate field group steps
    if (currentStepData.type === 'fieldGroup' && currentStepData.fieldGroup) {
      currentStepData.fieldGroup.fields?.forEach((field: any) => {
        if (field.defaultRequired && !memberData[field.fieldName]) {
          newErrors[field.fieldName] = `${field.label} is required`;
          isValid = false;
        }
      });
    }

    // Validate products step
    if (currentStepData.type === 'products') {
      if (productData.length === 0) {
        message.warning('Please select at least one product');
        isValid = false;
      }
    }

    // Validate locations step
    if (currentStepData.type === 'locations') {
      if (locationData.length === 0) {
        message.warning('Please add at least one location');
        isValid = false;
      }
    }

    // Validate documents step
    if (currentStepData.type === 'documents') {
      // Documents might be optional, check if any are required
      const requiredDocs = memberCategory?.documents?.filter((doc: any) => doc.isRequired);
      if (requiredDocs && requiredDocs.length > 0) {
        const uploadedDocTypes = documentData.map((doc: any) => doc.documentType);
        const missingRequired = requiredDocs.some((doc: any) => !uploadedDocTypes.includes(doc.documentType));
        if (missingRequired) {
          message.warning('Please upload all required documents');
          isValid = false;
        }
      }
    }

    setErrors(newErrors);
    
    if (isValid) {
      setCompletedSteps(prev => new Set(prev).add(currentStep));
    }
    
    return isValid;
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      if (currentStep < allSteps.length - 1) {
        setCurrentStep(currentStep + 1);
      }
    } else {
      message.error('Please complete all required fields');
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSave = () => {
    // Validate all field group steps
    let allValid = true;
    const allErrors: Record<string, string> = {};

    allSteps.forEach(step => {
      if (step.type === 'fieldGroup' && step.fieldGroup) {
        step.fieldGroup.fields?.forEach((field: any) => {
          if (field.defaultRequired && !memberData[field.fieldName]) {
            allErrors[field.fieldName] = `${field.label} is required`;
            allValid = false;
          }
        });
      }
    });

    if (!allValid) {
      setErrors(allErrors);
      message.error('Please fill in all required fields across all steps');
      return;
    }

    // Create member object with all data
    const member: GroupMemberData = {
      id: editingMember?.id || `member-${Date.now()}`,
      data: memberData,
      products: productData,
      locations: locationData,
      documents: documentData,
      isLeader: editingMember?.isLeader || false,
    };

    onSave(member, editingIndex);
    handleClose();
  };

  const handleClose = () => {
    setCurrentStep(0);
    setMemberData({});
    setProductData([]);
    setLocationData([]);
    setDocumentData([]);
    setErrors({});
    setCompletedSteps(new Set());
    onCancel();
  };

  const getStepStatus = (stepIndex: number): 'finish' | 'process' | 'wait' | 'error' => {
    if (completedSteps.has(stepIndex)) return 'finish';
    if (stepIndex === currentStep) return 'process';
    if (stepIndex < currentStep) {
      // Check if step has errors
      const step = allSteps[stepIndex];
      if (step?.type === 'fieldGroup' && step.fieldGroup) {
        const hasErrors = step.fieldGroup.fields?.some((field: any) => errors[field.fieldName]);
        return hasErrors ? 'error' : 'wait';
      }
    }
    return 'wait';
  };

  const renderStepContent = () => {
    if (fieldsLoading || categoryLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <p style={{ marginTop: 16 }}>Loading member form...</p>
        </div>
      );
    }

    if (fieldsError) {
      return (
        <Alert
          message="Configuration Error"
          description="Unable to load member form configuration. Please check the member template settings."
          type="error"
          showIcon
        />
      );
    }

    if (!allSteps || allSteps.length === 0) {
      return (
        <Alert
          message="No Configuration"
          description="The member template has no configuration. Please configure fields, products, locations, and documents."
          type="warning"
          showIcon
        />
      );
    }

    const currentStepData = allSteps[currentStep];
    if (!currentStepData) {
      return <Alert message="Invalid step" type="error" />;
    }

    // Render based on step type
    switch (currentStepData.type) {
      case 'fieldGroup':
        return (
          <SingleFieldGroupForm
            fieldGroup={currentStepData.fieldGroup}
            formData={memberData}
            onFieldChange={handleFieldChange}
            errors={errors}
            readonly={false}
          />
        );

      case 'products':
        return (
          <ProductSelectionForm
            categoryProducts={memberCategory?.products||[]}
            selectedProducts={productData}
            onProductsChange={setProductData}
            errors={errors}
          />
        );

      case 'locations':
        return (
          <LocationSelectionForm
            categoryLocations={memberCategory?.locations || []}
            selectedLocations={locationData}
            onLocationsChange={setLocationData}
            errors={errors}
          />
        );

      case 'documents':
        return (
          <DocumentUploadForm
            categoryDocuments={memberCategory?.documents || []}
            selectedDocuments={documentData}
            onDocumentsChange={setDocumentData}
            errors={errors}
          />
        );

      default:
        return <Alert message="Unknown step type" type="error" />;
    }
  };

  const modalTitle = editingMember ? 'Edit Group Member' : 'Add Group Member';
  const totalSteps = allSteps.length;
  const isLastStep = currentStep === totalSteps - 1;

  return (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={handleClose}
      width={1200}
      footer={null}
      destroyOnClose
      className="group-member-modal"
    >
      <div className="member-modal-content">
        {/* Progress Steps */}
        {allSteps.length > 0 && (
          <div className="member-steps-container">
            <Steps
              current={currentStep}
              size="small"
              onChange={(step) => {
                // Allow navigation to completed steps or current step
                if (step <= currentStep || completedSteps.has(step)) {
                  setCurrentStep(step);
                }
              }}
            >
              {allSteps.map((step, index) => (
                <Step
                  key={step.id}
                  title={step.title}
                  description={step.description}
                  status={getStepStatus(index)}
                  icon={
                    completedSteps.has(index) ? (
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    ) : undefined
                  }
                />
              ))}
            </Steps>
          </div>
        )}

        {/* Step Content */}
        <div className="member-step-content">
          {renderStepContent()}
        </div>

        {/* Navigation Footer */}
        <div className="member-modal-footer">
          <Space>
            <Button onClick={handleClose}>
              Cancel
            </Button>
            
            {currentStep > 0 && (
              <Button onClick={handlePrevious}>
                Previous
              </Button>
            )}
            
            {!isLastStep && (
              <Button type="primary" onClick={handleNext}>
                Next
              </Button>
            )}
            
            {isLastStep && (
              <Button type="primary" onClick={handleSave}>
                {editingMember ? 'Update Member' : 'Add Member'}
              </Button>
            )}
          </Space>

          {/* Progress Indicator */}
          <div className="step-progress">
            Step {currentStep + 1} of {totalSteps}
            {completedSteps.size > 0 && (
              <span style={{ marginLeft: 8, color: '#52c41a' }}>
                ({completedSteps.size} completed)
              </span>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default GroupMemberModal;
